import { ReactNode, CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs/Tabs';
import Blade from '@/components/ui/Blade/Blade';
import SupportedDtoIn from '@/models/in/SupportedDtoIn';
import { SupportedAdditionalInfoDtoIn } from '@/models/in/SupportedAdditionalInfoDtoIn';
import { getExpirationStatus, getExpirationStatusColor } from '@/helpers/contactClassificationExpiration';
import { formatDateLocale } from '@/helpers/dateHelper';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import { queryClient } from '@/hooks/QueryClient';
import useFundContext from '@/contexts/Fund/useFundContext';
import CompletionUploadSection from '@/components/CompletionUploadSection/CompletionUploadSection';
import DownloadFileButton from '@/components/ui/DownloadFileButton/DownloadFileButton';
import userSupport from '@/assets/icons/user-support.svg';
import bank from '@/assets/icons/bank.svg';
import whatsapp from '@/assets/icons/whatsapp.svg';
import email from '@/assets/icons/email.svg';
import id2Icon from '@/assets/icons/id2.svg';
import calendar from '@/assets/icons/calendar.svg';
import approvedSupport from '@/assets/icons/approved-support.svg';
import home from '@/assets/icons/home.svg';
import { RelationshipStatus } from '@/models/common/RelationshipStatus';
import { DocumentStatus } from '@/models/common/DocumentStatus';
import styles from './AdditionalInfo.module.css';
import docsStyles from './DocsTable/DocsTable.module.css';
import SupportedClassificationsInfo from './SupportedClassificationsInfo/SupportedClassificationsInfo';
import BladeSimpleSection from '@/components/ui/BladeSimpleSection/BladeSimpleSection';
import IconWithText from '@/components/ui/IconWithText/IconWithText';
import useFundIsraeliIdChangeRequests from '@/hooks/useFundIsraeliIdChangeRequests';
import IsraeliIdChangeDecisionNotice from './IsraeliIdChangeDecisionNotice';
import RequestIsraeliIdChangeDialog from '../RequestIsraeliIdChangeDialog/RequestIsraeliIdChangeDialog';

interface AdditionalInfoProps {
  supported: SupportedDtoIn;
  handleXclick: () => void;
  onSupportedNameClick: (supportedName: string) => void;
  onRefreshList?: () => void;
}

const AdditionalInfo = ({ supported, handleXclick, onSupportedNameClick, onRefreshList }: AdditionalInfoProps) => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();

  console.log(onSupportedNameClick); // TODO: check if need the onSupportedNameClick and if not remove from all places in supporteds folder

  const { data: supportedAdditionalInfo, isLoading } = useGetAuthApi<SupportedAdditionalInfoDtoIn>(
    currentFund && supported ? `funds/${currentFund.id}/supporteds/${supported.id}/additional-info` : ''
  );

  const createdDate = new Date('2025-09-12'); // TODO: This should come from the supported data

  // HAT-1428: the ID is fixed by the office, on the coordinator's request. Next to it the coordinator
  // sees the way to ask, or that a request is waiting; a decision shows above the tabs until they
  // acknowledge it - then only the office's mail remembers it.
  const { isOn: canRequestIdChange, bySupported } = useFundIsraeliIdChangeRequests();
  const openIdChangeRequest = bySupported.get(supported.id) ?? null;
  const [isIdChangeDialogOpen, setIsIdChangeDialogOpen] = useState(false);

  const idChangeStatus = (() => {
    if (!canRequestIdChange) return null;
    if (openIdChangeRequest?.status === 'Pending') {
      // Opens the dialog, which offers to withdraw the request
      return (
        <button type="button" className={styles['id-change-link']} onClick={() => setIsIdChangeDialogOpen(true)}>
          {t('israeliIdChangeRequest.pending', { id: openIdChangeRequest.requestedIsraeliId })}
        </button>
      );
    }
    return (
      <button type="button" className={styles['id-change-link']} onClick={() => setIsIdChangeDialogOpen(true)}>
        {t('israeliIdChangeRequest.requestLink')}
      </button>
    );
  })();

  const sections: ReactNode[] = [];

  if (!isLoading) {
    // Payment Details Section
    if (supportedAdditionalInfo?.paymentInfos?.length) {
      sections.push(
        <BladeSimpleSection key="paymentDetails" title={t('paymentDetails')}>
          <div className="flex flex-col gap-3">
            {supportedAdditionalInfo.paymentInfos.map((paymentInfo, index) => (
              <div key={index} className="flex gap-2 items-start pt-0.5 pb-0.5">
                <img src={bank} alt="" className="p-0.5" />
                <div className="flex flex-col">
                  <span className="body-lr-regular text-(--font-body-1)">{paymentInfo.accountOwnerName}</span>
                  <span className="body-lr-regular text-(--font-body-1)">
                    {paymentInfo.accountNumber}/{paymentInfo.branchNumber}
                  </span>
                  <span className="body-lr-regular text-(--font-body-1)">
                    {paymentInfo.bankName} {paymentInfo.bankNumber}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </BladeSimpleSection>
      );
    }

    // Contact Info Section
    if (supported.phone || supported.email) {
      sections.push(
        <BladeSimpleSection key="contactInfo" title={t('contactInfo')}>
          <div className="flex flex-col">
            {supported.phone && <IconWithText icon={whatsapp} text={supported.phone} />}
            {supported.email && <IconWithText icon={email} text={supported.email} />}
          </div>
        </BladeSimpleSection>
      );
    }

    // Address Section
    if (supportedAdditionalInfo?.address) {
      const address = supportedAdditionalInfo.address;
      const addressParts = [[address.streetName, address.streetNumber].filter(Boolean).join(' '), address.city, address.zipCode].filter(Boolean);
      const addressText = addressParts.join(', ');

      if (addressText) {
        sections.push(
          <BladeSimpleSection key="address" title={t('address')}>
            <span className="body-lr-regular text-(--font-body-1)">{addressText}</span>
          </BladeSimpleSection>
        );
      }
    }

    // Children Section
    if (supportedAdditionalInfo?.children?.length) {
      const children = supportedAdditionalInfo.children;

      // Children list with basic details + per-child completion upload
      sections.push(
        <BladeSimpleSection key="children" title={t('children')}>
          <div className="flex flex-col gap-4">
            {children.map((child, index) => (
              <div key={index} className="flex flex-col gap-2">
                <div className="flex gap-2 items-center">
                  <div className="h-[15px] border-l-2 border-(--border-tertiary-strong)"></div>
                  {child.status === RelationshipStatus.Approved && <img src={approvedSupport} alt="" className="w-5 h-5" />}
                  <span className="body-lr-semibold text-(--font-body-1)">
                    {child.firstName} {child.lastName}
                  </span>
                </div>
                <div className="flex flex-col">
                  <IconWithText icon={id2Icon} text={child.israeliId} />
                  {child.birthDate && <IconWithText icon={calendar} text={formatDateLocale(child.birthDate, i18n.language)} />}
                  {child.livesWithSupported && <IconWithText icon={home} text={t('livesWithSupported')} />}
                </div>
                {child.status === RelationshipStatus.PendingCompletion && currentFund && (
                  <CompletionUploadSection
                    apiEndpoint={`funds/${currentFund.id}/supporteds/${supported.id}/children/${child.id}/completion-documents`}
                    onSuccess={() => {
                      queryClient.invalidateQueries({ queryKey: [`funds/${currentFund.id}/supporteds/${supported.id}/additional-info`] });
                      onRefreshList?.();
                    }}
                    showAlert
                  />
                )}
              </div>
            ))}
          </div>
        </BladeSimpleSection>
      );

      // Unified documents table across all children
      const allDocs = children.flatMap((child) => (child.documents ?? []).map((doc) => ({ ...doc, childName: child.firstName })));
      const hasAnyDetails = allDocs.some((d) => d.status || d.officeComment || d.userComment);

      if (allDocs.length > 0) {
        sections.push(
          <BladeSimpleSection key="childrenDocuments" title={t('documents')}>
            <table className={docsStyles['table']}>
              <thead className={docsStyles['table-thead']}>
                <tr>
                  <th className="body-xs-sm-bold">{t('childName')}</th>
                  <th className="body-xs-sm-bold w-[150px]">
                    {t('document')}/{t('approval')}
                  </th>
                  <th className="body-xs-sm-bold">{t('uploadDate')}</th>
                  {hasAnyDetails && (
                    <>
                      <th className="body-xs-sm-bold">{t('status')}</th>
                      <th className="body-xs-sm-bold">{t('officeComment')}</th>
                      <th className="body-xs-sm-bold">{t('userComment')}</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className={docsStyles['table-tbody']}>
                {allDocs.map((doc, idx) => (
                  <tr key={idx}>
                    <td className={docsStyles['table-cell-date']}>{doc.childName}</td>
                    <td className={docsStyles['table-cell-doc']}>
                      <div className="flex items-center gap-1">
                        <a href={doc.uri} target="_blank" rel="noreferrer" className="body-sm-link text-(--font-link)">
                          {doc.name}
                        </a>
                        <DownloadFileButton uri={doc.uri} fileName={doc.name} />
                      </div>
                    </td>
                    <td className={docsStyles['table-cell-date']}>{formatDateLocale(doc.creationTime, i18n.language)}</td>
                    {hasAnyDetails && (
                      <>
                        <td className={docsStyles['table-cell-date']}>
                          {doc.status === DocumentStatus.Rejected ? t('documentStatusRejected') : doc.status || '-'}
                        </td>
                        <td className={docsStyles['table-cell-date']}>{doc.officeComment || '-'}</td>
                        <td className={docsStyles['table-cell-date']}>{doc.userComment || '-'}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </BladeSimpleSection>
        );
      }

      // Unified office comments table across all children
      const allComments = children
        .flatMap((child) => (child.visibleOfficeComments ?? []).map((comment) => ({ ...comment, childName: child.firstName })))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      if (allComments.length > 0) {
        sections.push(
          <BladeSimpleSection key="childrenOfficeComments" title={t('officeComments.title')}>
            <table className={docsStyles['table']}>
              <thead className={docsStyles['table-thead']}>
                <tr>
                  <th className="body-xs-sm-bold">{t('childName')}</th>
                  <th className="body-xs-sm-bold">{t('officeComments.comment')}</th>
                  <th className="body-xs-sm-bold w-[100px]">{t('officeComments.date')}</th>
                  <th className="body-xs-sm-bold w-[100px]">{t('officeComments.sentOn')}</th>
                </tr>
              </thead>
              <tbody className={docsStyles['table-tbody']}>
                {allComments.map((comment, idx) => (
                  <tr key={idx}>
                    <td className={docsStyles['table-cell-date']}>{comment.childName}</td>
                    <td className={docsStyles['table-cell-doc']}>{comment.comment}</td>
                    <td className={docsStyles['table-cell-date']}>{formatDateLocale(comment.date, i18n.language)}</td>
                    <td className={docsStyles['table-cell-date']}>{comment.sendDate ? formatDateLocale(comment.sendDate, i18n.language) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </BladeSimpleSection>
        );
      }
    }
  }

  const headerTop = (
    <div className="flex items-center gap-3">
      <img src={userSupport} alt="" className="w-6 h-6" />
      <div className="flex flex-col">
        <span className="body-lr-semibold text-(--font-body-4)">
          {supported.firstName} {supported.lastName}
        </span>
        <span className="body-sm-regular text-(--font-body-4)">{supported.israeliId}</span>
        {idChangeStatus}
      </div>
    </div>
  );

  const headerBottom = (
    <div className="flex items-start w-full justify-between gap-3">
      <div className="flex flex-wrap gap-2 min-w-0">
        {supported.contactClassifications.map((contactClassification, index) => {
          const expirationStatus = getExpirationStatus(contactClassification);
          const color = getExpirationStatusColor(expirationStatus);

          return (
            <span key={index} className={`${styles.classification} body-md-medium`}>
              <span className={styles['classification-color']} style={{ '--classification-color': color } as CSSProperties}></span>
              {t(contactClassification.classification.name)}
            </span>
          );
        })}
      </div>
      <div className="flex flex-col shrink-0 text-(--font-body-4) body-xs-sm-regular">
        <span>{t('addedOn')}</span>
        <span>
          {Intl.DateTimeFormat('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }).format(createdDate)}
        </span>
      </div>
    </div>
  );

  return (
    <Blade handleXclick={handleXclick} headerTop={headerTop} headerBottom={headerBottom} width="520px">
      {canRequestIdChange && currentFund && openIdChangeRequest && openIdChangeRequest.status !== 'Pending' && (
        <IsraeliIdChangeDecisionNotice
          fundId={currentFund.id}
          request={openIdChangeRequest}
          onRequestAgain={() => setIsIdChangeDialogOpen(true)}
        />
      )}
      <Tabs defaultValue="classificationsSupports" style={{ direction: 'inherit' }}>
        <TabsList>
          <TabsTrigger value="classificationsSupports">{t('classificationsAndSupports')}</TabsTrigger>
          <TabsTrigger value="additionalInfo">{t('additionalInfo')}</TabsTrigger>
        </TabsList>
        <TabsContent value="classificationsSupports" className={`${styles.content} custom-scrollbar`}>
          <SupportedClassificationsInfo supported={supported} onRefreshList={onRefreshList} />
        </TabsContent>
        <TabsContent value="additionalInfo" className={`${styles.content} custom-scrollbar`}>
          {sections.map((section, index, arr) => (
            <>
              {section}
              {index !== arr.length - 1 && <div className={styles.line} />}
            </>
          ))}
        </TabsContent>
      </Tabs>
      {canRequestIdChange && currentFund && (
        <RequestIsraeliIdChangeDialog
          fundId={currentFund.id}
          supportedId={supported.id}
          supportedName={`${supported.firstName} ${supported.lastName}`.trim()}
          currentIsraeliId={supported.israeliId}
          open={isIdChangeDialogOpen}
          setOpen={setIsIdChangeDialogOpen}
        />
      )}
    </Blade>
  );
};

export default AdditionalInfo;
